import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "./../components/ui/card";
import { LogIn } from "lucide-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import appNameLogo from "./../images/appName.PNG";
import dashboardOne from "./../images/dashboardOne.PNG";
import dashboardTwo from "./../images/dashboardTwo.PNG";
import dashboardThree from "./../images/dashboardThree.PNG";
import dashboardFour from "./../images/dashboardFour.PNG";
import dashboardFive from "./../images/dashboardFive.PNG";
import dashboardSix from "./../images/dashboardSix.PNG";

const LoginPage = () => {
  const { loginWithPopup } = useAuth0();
  const [currentSlide, setCurrentSlide] = useState(0);

  // Mock dashboard images - replace these with your actual dashboard screenshots
  const slides = [
    dashboardOne,
    dashboardTwo,
    dashboardThree,
    dashboardFour,
    dashboardFive,
    dashboardSix,
  ];

  // Auto-advance slides
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleSignup = async () => {
    try {
      await loginWithPopup({
        authorizationParams: {
          connection: "Username-Password-Authentication",
          screen_hint: "signup",
        },
      });
    } catch (error) {
      console.error("Error during sign up:", error);
    }
  };

  const handleEmailLogin = async () => {
    try {
      await loginWithPopup({
        authorizationParams: {
          connection: "Username-Password-Authentication",
          screen_hint: "login",
        },
      });
    } catch (error) {
      console.error("Error during email login:", error);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await loginWithPopup({
        authorizationParams: {
          prompt: "login",
          connection: "google-oauth2",
        },
      });
    } catch (error) {
      console.error("Error during login:", error);
    }
  };

  const handleFacebookLogin = async () => {
    try {
      await loginWithPopup({
        authorizationParams: {
          connection: "facebook",
        },
      });
    } catch (error) {
      console.error("Error during Facebook login:", error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col md:flex-row">
      {/* Left Side - Image Slider */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-4 md:p-8 flex flex-col order-2 md:order-2">
        <div className="text-center mb-6 text-white">
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Streamline Your Casino Success Journey
          </h1>
          <p className="text-xl text-gray-300" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Never miss a reward—keep tabs on your deposits, withdrawals, and
            sessions all in one place
          </p>
        </div>
        <div className="relative bg-slate-800 rounded-xl overflow-hidden w-4/5 mx-auto">
          <div
            className="flex items-center justify-center"
            style={{ maxHeight: "400px", height: "100%" }}
          >
            <img
              src={slides[currentSlide]}
              alt={`Dashboard example ${currentSlide + 1}`}
              className="w-full object-contain"
            />
          </div>

          {/* Slider Controls */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-slate-900/50 p-2 rounded-full text-white hover:bg-slate-900/75 transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-slate-900/50 p-2 rounded-full text-white hover:bg-slate-900/75 transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Slide Indicators */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
            {slides.map((_, index) => (
              <button
                key={index}
                className={`w-2 h-2 rounded-full transition-colors ${
                  currentSlide === index ? "bg-white" : "bg-white/50"
                }`}
                onClick={() => setCurrentSlide(index)}
              />
            ))}
          </div>
        </div>

        <div className="text-center mt-6">
          <p className="text-xl text-white" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            Start managing your casino rewards today!
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-4 md:p-8 bg-white order-1 and md:order-1">
        <div className="flex flex-col items-center w-full max-w-md">
          <img src={appNameLogo} alt="App Logo" />

          <Card className="w-full max-w-md border border-gray-200 shadow-lg">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl font-bold text-slate-900">
                Sign In
              </CardTitle>
              <p className="text-slate-600 mt-2">Access your dashboard</p>
            </CardHeader>
            <CardContent className="space-y-4 p-6">
              <button
                onClick={handleEmailLogin}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <LogIn className="w-5 h-5" />
                <span>Sign in with Email</span>
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">Or</span>
                </div>
              </div>

              <button
                onClick={handleGoogleLogin}
                className="w-full flex items-center justify-center gap-2 bg-white text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors border border-gray-300"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span>Continue with Google</span>
              </button>

              <button
                onClick={handleFacebookLogin}
                className="w-full flex items-center justify-center gap-2 bg-white text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors border border-gray-300"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#1877F2"
                    d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                  />
                </svg>
                <span>Continue with Facebook</span>
              </button>
            </CardContent>
            <div className="text-center text-gray-500">
              {" "}
              {/* Container for the text and link */}
              Don't you already have an account?{" "}
              <button
                onClick={handleSignup}
                className="text-blue-500 hover:underline focus:outline-none"
              >
                {/* Link to Sign Up */}
                Sign Up
              </button>{" "}
            </div>
            <div className="py-2"></div> {/* Adds vertical padding */}
            <CardFooter className="text-center text-sm text-gray-500 px-6 pb-6">
              By signing in, you agree to our Terms of Service and Privacy
              Policy
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
